import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthFetch } from '../lib/useAuthFetch'
import { Card, PageHeader, SectionLabel } from '../components/ui'

const API = import.meta.env.VITE_API_URL ?? ''

interface Preview {
  billing: {
    billing_status: string
    billing_period_end: string | null
    billing_cancel_at_period_end: boolean
  }
  locations: { location_id: string; location_name: string }[]
  pricing: { monthlyTotalCents: number | null } | null
}

const money = (cents: number | null | undefined) => cents == null ? '—' : new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
}).format(cents / 100)

export default function CancellationFlow() {
  const navigate = useNavigate()
  const authFetch = useAuthFetch()
  const [preview, setPreview] = useState<Preview | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    authFetch(`${API}/api/v1/billing/plan-preview`)
      .then(async (response) => {
        const body = await response.json()
        if (!response.ok || !body?.success) throw new Error(body?.error || 'Could not load subscription details.')
        setPreview(body.data)
      })
      .catch((reason) => setError(reason?.message || 'Could not load subscription details.'))
      .finally(() => setLoading(false))
  }, [authFetch])

  async function cancel() {
    if (!acknowledged || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const response = await authFetch(`${API}/api/v1/billing/cancel`, { method: 'POST', body: JSON.stringify({ reason: 'customer_requested' }) })
      const body = await response.json()
      if (!response.ok || !body?.success) throw new Error(body?.error || 'Could not cancel the subscription.')
      setConfirmed(true)
    } catch (reason: any) {
      setError(reason?.message || 'Could not cancel the subscription.')
    } finally {
      setSubmitting(false)
    }
  }

  if (confirmed) {
    return <div style={{ minHeight: '100%', padding: 24, background: 'var(--bg)' }}><Card style={{ maxWidth: 620, margin: '48px auto', padding: 28 }}><h1 style={{ marginTop: 0 }}>Renewal canceled</h1><p style={{ color: 'var(--text-2)', lineHeight: 1.6 }}>You keep access through the end of the paid month. Your locations, tracked rivals and history remain saved.</p><button className="btn btn-primary" onClick={() => navigate('/billing')}>Back to billing</button></Card></div>
  }

  const endDate = preview?.billing.billing_period_end ? new Date(preview.billing.billing_period_end).toLocaleDateString() : 'the end of the paid month'

  return (
    <div style={{ minHeight: '100%', background: 'var(--bg)', overflowY: 'auto' }}>
      <PageHeader variant="compact" title="Cancel subscription" subtitle="Review what happens before you confirm" onBack={() => navigate('/billing')} />
      <main style={{ maxWidth: 720, margin: '0 auto', padding: 24 }}>
        {loading && <Card>Loading subscription details…</Card>}
        {error && <div role="alert" style={{ marginBottom: 14, color: 'var(--danger)' }}>{error}</div>}
        {!loading && preview && (
          <>
            <Card style={{ padding: 20, marginBottom: 16 }}>
              <SectionLabel>Current subscription</SectionLabel>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}><span>{preview.locations.length} active location{preview.locations.length === 1 ? '' : 's'}</span><strong>{money(preview.pricing?.monthlyTotalCents)}/month</strong></div>
            </Card>
            <Card style={{ padding: 20, marginBottom: 16 }}>
              <SectionLabel>What happens</SectionLabel>
              {[
                `Your subscription will not renew after ${endDate}.`,
                'There is no prorated cash refund for the current paid month.',
                'Your locations, tracked rivals and historical data stay saved.',
                'You can reactivate later without rebuilding your account.',
              ].map((item) => <div key={item} style={{ marginTop: 9, fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6 }}>• {item}</div>)}
            </Card>
            <label style={{ display: 'flex', gap: 10, padding: 14, border: '1px solid var(--border)', borderRadius: 8, marginBottom: 16, cursor: 'pointer' }}>
              <input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} />
              <span style={{ fontSize: 12, lineHeight: 1.5 }}>I understand that renewal will be canceled and my configuration will remain saved.</span>
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" onClick={() => navigate('/billing')}>Keep subscription</button>
              <button className="btn" disabled={!acknowledged || submitting} onClick={cancel} style={{ flex: 1, color: 'var(--rose)' }}>{submitting ? 'Cancelling…' : 'Cancel renewal'}</button>
            </div>
          </>
        )}
      </main>
    </div>
  )
}
